import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from "@aix/ui"
import { AlertCircle, CheckCircle, Clock, Pause, Play, RefreshCw, XCircle } from "lucide-react"
import React, { useEffect } from "react"
import toast from "react-hot-toast"
import { trpc } from "~/lib/trpc"

export const QueueMonitor: React.FC = () => {
  const { data: queueStats, refetch: refetchStats } = trpc.emailMarketing.getQueueStats.useQuery()
  const { data: sendingQuota, refetch: refetchQuota } = trpc.emailMarketing.getSendingQuota.useQuery()

  const processQueue = trpc.emailMarketing.processQueue.useMutation({
    onSuccess: (result) => {
      toast.success(result.message)
      void refetchStats()
      void refetchQuota()
    },
    onError: (error) => {
      toast.error(`Failed to process queue: ${error.message}`)
    },
  })

  const pauseQueue = trpc.emailMarketing.pauseQueue.useMutation({
    onSuccess: () => {
      toast.success("Queue paused")
      void refetchStats()
    },
    onError: (error) => {
      toast.error(`Failed to pause queue: ${error.message}`)
    },
  })

  const resumeQueue = trpc.emailMarketing.resumeQueue.useMutation({
    onSuccess: () => {
      toast.success("Queue resumed")
      void refetchStats()
    },
    onError: (error) => {
      toast.error(`Failed to resume queue: ${error.message}`)
    },
  })

  // Auto-refresh stats every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      void refetchStats()
      void refetchQuota()
    }, 5000)

    return () => clearInterval(interval)
  }, [refetchStats, refetchQuota])

  const getQueueStatusColor = (status: string, count: number) => {
    if (count === 0) return "text-muted-foreground"

    switch (status) {
      case "pending":
        return "text-yellow-600"
      case "processing":
        return "text-blue-600"
      case "completed":
        return "text-green-600"
      case "failed":
        return "text-red-600"
      case "delayed":
        return "text-orange-600"
      default:
        return "text-foreground"
    }
  }

  const getQueueStatusIcon = (status: string) => {
    switch (status) {
      case "pending":
        return <Clock className="w-5 h-5" />
      case "processing":
        return <RefreshCw className="w-5 h-5 animate-spin" />
      case "completed":
        return <CheckCircle className="w-5 h-5" />
      case "failed":
        return <XCircle className="w-5 h-5" />
      case "delayed":
        return <AlertCircle className="w-5 h-5" />
      default:
        return null
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Queue Status</CardTitle>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => processQueue.mutate()}
                loading={processQueue.isLoading}
                size="sm"
                variant="outline"
              >
                <Play className="w-4 h-4 mr-2" />
                Process Now
              </Button>
              <Button onClick={() => pauseQueue.mutate()} loading={pauseQueue.isLoading} size="sm" variant="outline">
                <Pause className="w-4 h-4 mr-2" />
                Pause
              </Button>
              <Button onClick={() => resumeQueue.mutate()} loading={resumeQueue.isLoading} size="sm" variant="outline">
                <Play className="w-4 h-4 mr-2" />
                Resume
              </Button>
              <Button
                onClick={() => {
                  void refetchStats()
                  void refetchQuota()
                }}
                size="sm"
                variant="outline"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {queueStats &&
              Object.entries(queueStats).map(([status, count]) => (
                <div key={status} className="p-4 border border-border rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <div className={getQueueStatusColor(status, count)}>{getQueueStatusIcon(status)}</div>
                    <span className="text-sm font-medium capitalize">{status}</span>
                  </div>
                  <p className={`text-2xl font-bold ${getQueueStatusColor(status, count)}`}>{count}</p>
                </div>
              ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>AWS SES Quota</CardTitle>
        </CardHeader>
        <CardContent>
          {sendingQuota ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">24 Hour Limit</p>
                  <p className="text-xl font-semibold">{sendingQuota.max24HourSend?.toLocaleString()}</p>
                </div>

                <div>
                  <p className="text-sm text-muted-foreground">Sent (24h)</p>
                  <p className="text-xl font-semibold">{sendingQuota.sentLast24Hours?.toLocaleString()}</p>
                </div>

                <div>
                  <p className="text-sm text-muted-foreground">Remaining Today</p>
                  <p className="text-xl font-semibold text-green-600">
                    {sendingQuota.remainingToday?.toLocaleString()}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-muted-foreground">Send Rate</p>
                  <p className="text-xl font-semibold">{sendingQuota.maxSendRate}/sec</p>
                </div>
              </div>

              <div className="pt-4 border-t border-border">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Usage</span>
                  <span className="text-sm font-medium">
                    {(((sendingQuota.sentLast24Hours || 0) / (sendingQuota.max24HourSend || 1)) * 100).toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-secondary rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${Math.min(
                        ((sendingQuota.sentLast24Hours || 0) / (sendingQuota.max24HourSend || 1)) * 100,
                        100,
                      )}%`,
                    }}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">Loading quota information...</div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Queue Processing Info</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>• Queue processes automatically every minute via cron job</p>
            <p>• Emails are sent at 14 per second (AWS SES default rate)</p>
            <p>• Failed emails retry with exponential backoff (max 3 retries)</p>
            <p>• Click &quot;Process Now&quot; to manually trigger queue processing</p>
            <p>• Pause/Resume controls affect automatic processing</p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
